import { configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
import createIndexedDBStorage from "redux-persist-indexeddb-storage";
import { createStateSyncMiddleware, initMessageListener } from "redux-state-sync";

import { loggedOut } from "./actions";
import { accountAdded, accountUpdated, accountVerified } from "./reducers/account";
import { criteriaCatalogAdded, specificCriteriaCatalogAdded } from "./reducers/criteriaCatalog";
import { occupationAdded } from "./reducers/occupation";
import { requirementsAdded } from "./reducers/requirements";

import combinedReducers from "./index";

const persistConfig = {
  key: "root",
  stateReconciler: autoMergeLevel2,
  storage: createIndexedDBStorage("scopeXDB"),
  // Only the session and the catalogs survive a reload; pages refetch everything else
  whitelist: ["account", "criteriaCatalog", "occupation", "requirements"],
  // At most one IndexedDB write per second
  throttle: 1000,
};

// Only actions on state that is the same in every tab are replayed in the other open tabs: the
// session and the catalogs. All other slices hold the page a tab has open; replaying those let one
// tab overwrite another tab's facility or process with its own, and cloned every API response
// into every tab. redux-persist's own actions stay out too — they carry callbacks that
// BroadcastChannel can't clone (DataCloneError on logout).
const SYNCED_ACTION_TYPES = new Set([
  loggedOut.type,
  accountAdded.type,
  accountVerified.type,
  accountUpdated.type,
  criteriaCatalogAdded.type,
  specificCriteriaCatalogAdded.type,
  occupationAdded.type,
  requirementsAdded.type,
]);

const stateSyncMiddleware = createStateSyncMiddleware({
  // A predicate rather than `whitelist`, which redux-state-sync silently ignores once a
  // `blacklist` is set
  predicate: action => SYNCED_ACTION_TYPES.has(action.type),
});

const store = configureStore({
  reducer: persistReducer(persistConfig, combinedReducers),
  // DevTools serialize the whole state on every action
  devTools: import.meta.env.DEV,
  // Thunk is part of the defaults. The two checks run in development only (RTK leaves them out
  // of production builds); walking this much state can exceed the default 32 ms warning.
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        warnAfter: 128,
      },
    }).concat(stateSyncMiddleware),
});

const persistor = persistStore(store);

initMessageListener(store);

export { store, persistor };
